import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "../i18n";
import { RESTAURANT } from "../data";
import { useSettings } from "../SiteSettings";

const SECTIONS = ["about", "dishes", "gallery", "hours", "reviews", "contact"];
const DESKTOP_SECTIONS = ["about", "hours", "contact"];

export const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const settings = useSettings();
  const R = { ...RESTAURANT, ...(settings.contact || {}) };
  const vacActive = !!(settings.vacation && settings.vacation.active);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/#" + id);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const goMenu = () => {
    setOpen(false);
    navigate("/speisekarte");
  };

  const goEvents = () => {
    setOpen(false);
    navigate("/veranstaltungen");
  };

  const goVouchers = () => {
    setOpen(false);
    navigate("/gutscheine");
  };

  const darkHeader = location.pathname === "/" || location.pathname === "/speisekarte";
  const dark = !scrolled && darkHeader;

  return (
    <header
      data-testid="navbar"
      style={{ top: vacActive ? 41 : 0 }}
      className={`fixed w-full z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-qs-bg/90 border-b border-qs-border py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => (location.pathname === "/" ? window.scrollTo({ top: 0, behavior: "smooth" }) : navigate("/"))}
          className="flex items-center group"
          aria-label="Pizzeria Quattro Stelle"
        >
          <img
            src="/logo-mark-white.png"
            alt="Pizzeria Quattro Stelle Logo"
            className={`w-auto object-contain transition-all duration-300 ${scrolled ? "h-11 md:h-12" : "h-12 md:h-14"}`}
          />
        </button>

        <nav className="hidden lg:flex items-center gap-6">
          {DESKTOP_SECTIONS.map((s) => (
            <button
              key={s}
              data-testid={`nav-${s}`}
              onClick={() => goSection(s)}
              className={`font-outfit text-sm tracking-wide transition-colors hover:text-qs-gold ${
                dark ? "text-white/90" : "text-qs-text"
              }`}
            >
              {t.nav[s]}
            </button>
          ))}
          <button
            data-testid="nav-menu-page"
            onClick={goMenu}
            className={`font-outfit text-sm tracking-wide transition-colors hover:text-qs-gold ${
              dark ? "text-white/90" : "text-qs-text"
            }`}
          >
            {t.nav.menu}
          </button>
          <button
            data-testid="nav-events"
            onClick={goEvents}
            className={`font-outfit text-sm tracking-wide transition-colors hover:text-qs-gold ${
              dark ? "text-white/90" : "text-qs-text"
            }`}
          >
            {t.nav.events}
          </button>
          <button
            data-testid="nav-vouchers"
            onClick={goVouchers}
            className={`font-outfit text-sm tracking-wide transition-colors hover:text-qs-gold ${
              dark ? "text-white/90" : "text-qs-text"
            }`}
          >
            {t.nav.vouchers}
          </button>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className={`hidden sm:flex items-center gap-1 text-xs font-semibold tracking-widest uppercase border rounded-full px-4 py-1.5 transition-colors ${
              dark
                ? "border-white/40 text-white hover:bg-white/10"
                : "border-qs-border text-qs-text hover:border-qs-gold"
            }`}
          >
            {lang === "de" ? "EN" : "DE"}
          </button>
          <button
            data-testid="nav-reserve"
            onClick={() => goSection("contact")}
            className="hidden md:inline-flex items-center bg-qs-red text-white hover:bg-qs-reddark transition-colors rounded-full px-6 py-2.5 text-xs font-semibold tracking-[0.12em] uppercase"
          >
            {t.nav.reserve}
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((p) => !p)}
            className={`lg:hidden p-1 ${dark ? "text-white" : "text-qs-text"}`}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden overflow-hidden transition-all duration-500 bg-qs-bg border-b border-qs-border ${
          open ? "max-h-[520px]" : "max-h-0"
        }`}
      >
        <nav className="flex flex-col px-6 py-4">
          {SECTIONS.map((s) => (
            <button
              key={s}
              data-testid={`mobile-nav-${s}`}
              onClick={() => goSection(s)}
              className="text-left py-3 border-b border-qs-border/60 font-outfit text-qs-text hover:text-qs-gold transition-colors"
            >
              {t.nav[s]}
            </button>
          ))}
          <button
            onClick={goMenu}
            data-testid="mobile-nav-menu"
            className="text-left py-3 border-b border-qs-border/60 font-outfit text-qs-text hover:text-qs-gold transition-colors"
          >
            {t.nav.menu}
          </button>
          <button
            onClick={goEvents}
            data-testid="mobile-nav-events"
            className="text-left py-3 border-b border-qs-border/60 font-outfit text-qs-text hover:text-qs-gold transition-colors"
          >
            {t.nav.events}
          </button>
          <button
            onClick={goVouchers}
            data-testid="mobile-nav-vouchers"
            className="text-left py-3 border-b border-qs-border/60 font-outfit text-qs-text hover:text-qs-gold transition-colors"
          >
            {t.nav.vouchers}
          </button>
          <div className="flex items-center gap-3 pt-4">
            <button
              onClick={toggle}
              data-testid="mobile-lang-toggle"
              className="flex-1 border border-qs-border rounded-full py-2.5 text-xs font-semibold tracking-widest uppercase"
            >
              {lang === "de" ? "English" : "Deutsch"}
            </button>
            <a
              href={`tel:${R.phoneIntl}`}
              className="flex-1 flex items-center justify-center gap-2 bg-qs-red text-white rounded-full py-2.5 text-xs font-semibold tracking-widest uppercase"
            >
              <Phone size={14} /> {R.phone}
            </a>
          </div>
          <button
            onClick={() => goSection("contact")}
            data-testid="mobile-nav-reserve"
            className="mt-3 bg-qs-gold text-black rounded-full py-3 text-xs font-semibold tracking-[0.12em] uppercase"
          >
            {t.nav.reserve}
          </button>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
